import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { TextField, TextFieldProps } from '@mui/material'
import { ChangeEvent } from 'react'
import cn from 'classnames'

interface TextFieldControllerProps<T extends FieldValues>
  extends Omit<TextFieldProps, 'name' | 'defaultValue'> {
  control: Control<T>
  name: Path<T>
  maxLength?: number
  errorMessage?: string
  disabled?: boolean
  onValueChange?: (value: string) => void
  mask?: (value: string) => string
}

export const TextFieldController = <T extends FieldValues>({
  control,
  name,
  maxLength,
  disabled,
  errorMessage,
  onValueChange,
  mask,
  ...rest
}: TextFieldControllerProps<T>) => {
  const handleValueChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    onChange: (value: string) => void
  ) => {
    let value = event.target.value

    if (maxLength) {
      value = value.slice(0, maxLength)
    }

    if (mask) {
      value = mask(value)
    }

    onChange(value)

    if (onValueChange) onValueChange(value)
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <TextField
          {...field}
          {...rest}
          value={mask ? mask(field.value) : field.value}
          disabled={disabled}
          className={cn('w-full', rest.className)}
          InputLabelProps={{
            style: {
              fontWeight: 'bold',
              fontFamily: 'BB-Font-Regular'
            }
          }}
          error={rest.error ? rest.error : !!errorMessage}
          helperText={errorMessage}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            handleValueChange(e, field.onChange)
          }
          inputProps={{
            ...rest.inputProps,
            maxLength: maxLength,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              e.target.value = e.target.value.slice(0, maxLength)
              rest.inputProps?.onChange?.(e)
            }
          }}
        />
      )}
    />
  )
}

export default TextFieldController
